import { toast, Typography, useMediaQuery } from "@kyan/ui";
import { type Address } from "viem";

import { useOpenCardIds } from "#/hooks/useOpenCardIds";
import { useRedeemersTradeStats } from "#/services/kyan/hooks/useTradeStats";
import { useDeleteReferral } from "#/services/referrals/hooks/useDeleteReferral";
import { useReferralsByAccount } from "#/services/referrals/hooks/useReferralsByAccount";
import { ReferralsDetails } from "#/types/referrals";
import { confirm } from "#/utils/modals/confirm";

import { getColumns } from "./Referrals.config";

export const useReferralsController = () => {
  const { isSm } = useMediaQuery();
  const { openCardIds, onCardClick } = useOpenCardIds<ReferralsDetails>();
  const { redeemers, referrals, isLoading } = useReferralsByAccount();
  const {
    data: { referralsDetails },
    isLoading: isTradeStatsLoading,
  } = useRedeemersTradeStats(redeemers);
  const { mutate: deleteReferral } = useDeleteReferral({
    onSuccess: () => {
      toast.success({
        title: "Referral deleted successfully",
      });
    },
  });

  const handleDeleteReferral = (redeemerAddress: string) => {
    const referral = referrals.find((referral) =>
      referral.redeemers?.some(
        (redeemer) => redeemer.usedBy === redeemerAddress,
      ),
    );
    if (!referral) {
      return;
    }
    confirm({
      header: "Confirm revoke and delete referrals",
      headerClassName: "text-textPrimary text-lg",
      content: (
        <div className="grid gap-4">
          <Typography>
            Are you sure you want to delete{" "}
            <Typography medium primary>
              {referral.code}{" "}
            </Typography>
            ?
          </Typography>
          <Typography>
            This is irreversible, so make sure to confirm that you want to
            delete the referral.
          </Typography>
        </div>
      ),
      confirmText: "Yes, delete",
      onConfirm: async () => {
        await deleteReferral({
          code: referral.code,
          redeemerAddress: redeemerAddress as Address,
        });
      },
    });
  };

  const columns = getColumns({ isSm, handleDeleteReferral });

  return {
    referralsDetails,
    openCardIds,
    onCardClick,
    isLoading: isLoading || isTradeStatsLoading,
    columns,
  };
};
